"use client";

import { type CSSProperties, useState } from "react";

import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";

import {
  BookOutlined,
  HomeFilled,
  MenuOutlined,
  PlusCircleOutlined,
  SearchOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useClerk } from "@clerk/nextjs";
import { Building20Filled } from "@fluentui/react-icons";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  type VariantProps,
  tv,
} from "@heroui/react";
import { IconLogout } from "@tabler/icons-react";
import { theme } from "antd";

import MenuItem, { type MenuItemProps } from "@/components/atoms/menu-item";

const headerVariants = tv({
  base: "z-20 flex justify-between text-dr-white-600 lg:hidden",
  variants: {
    type: {
      Talento: "bg-lilac-geode-1000",
      Lead: "bg-brand-dark",
      Gestor: "bg-brand-dark",
      Representante: "bg-brand-dark",
      Hunter_Externo: "bg-brand-dark",
      Hunter_Interno: "bg-brand-dark",
      Owner: "bg-brand-dark",
      Owner_Externo: "bg-brand-dark",
      Scout: "bg-brand-dark",
    },
  },
  defaultVariants: {
    type: "Talento",
  },
});

type HeaderVariantProps = VariantProps<typeof headerVariants>;

const Header = ({ type }: HeaderVariantProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { token } = theme.useToken();
  const router = useRouter();
  const { signOut } = useClerk();

  const onClick = (route: string) => {
    router.push(route);
    setIsOpen(false);
  };

  const style: CSSProperties = { color: token.colorPrimary };

  const items: MenuItemProps[] = [
    {
      icon: <HomeFilled style={style} />,
      label: "Início",
      onClick: () => onClick("/"),
    },
  ];

  if (type === "Talento") {
    items.push({
      icon: <UserOutlined style={style} />,
      label: "Minha conta",
      onClick: () => onClick("/profile"),
    });
    items.push({
      icon: <SearchOutlined style={style} />,
      label: "Novas vagas",
    });
  }

  if (["Gestor", "Representante"].includes(String(type))) {
    // items.push({
    //   icon: <CommentOutlined style={style} />,
    //   label: "Chat",
    // });
    // items.push({
    //   icon: <CalendarOutlined style={style} />,
    //   label: "Agenda",
    // });
    items.push({
      icon: <BookOutlined style={style} />,
      label: "Histórico",
      onClick: () => onClick("/job/historico"),
    });
    items.push({
      icon: <PlusCircleOutlined style={style} />,
      label: "Nova Vaga",
      onClick: () => onClick("/abertura-de-vagas"),
    });
    items.push({
      icon: <Building20Filled className="-mr-1" />,
      label: "Perfil Empresa",
      onClick: () => onClick("/profile/company"),
    });
  }

  if (
    ["Owner", "Owner_Externo", "Hunter_Interno", "Hunter_Externo"].includes(
      String(type),
    )
  ) {
    items.push({
      icon: <BookOutlined style={style} />,
      label: "Histórico",
      onClick: () => onClick("/job/historico"),
    });
  }

  if (["Gestor", "Representante"].includes(String(type))) {
    items.push({
      icon: <UserOutlined style={style} />,
      label: "Meu Perfil",
      onClick: () => onClick("/profile"),
    });
  }

  if (["Lead"].includes(String(type))) {
    items.push({
      icon: <PlusCircleOutlined style={style} />,
      label: "Nova Vaga",
      onClick: () => onClick("/abertura-de-vagas"),
    });
  }

  // Logout
  items.push({
    icon: <IconLogout style={style} size={20} />,
    label: "Sair",
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    onClick: () => signOut({ redirectUrl: "/" }),
  });

  return (
    <header className={headerVariants({ type })}>
      <Link href="/" className="p-4">
        <Image
          src="/logo-white-text-full.svg"
          alt="Harpio"
          width={70}
          height={25}
        />
      </Link>
      <Popover
        placement="bottom-start"
        offset={-4}
        containerPadding={0}
        isOpen={isOpen}
        onOpenChange={() => setIsOpen((prev) => !prev)}
      >
        <PopoverTrigger>
          <MenuOutlined className="m-0 p-4 text-2xl text-white" />
        </PopoverTrigger>
        <PopoverContent>
          <div className="gap-2 bg-lilac-geode-200 shadow-md">
            {items.map((item) => (
              <MenuItem key={item.label} {...item} />
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </header>
  );
};

export default Header;
